import { useEffect, useRef, useState } from 'react';
import PowerBIReport from '../../components/PowerBIReport';
import { useAuthStore } from '../../features/authentication/stores/authStore';
import { models, Page, Report } from 'powerbi-client';
import { ApprovalStatus, Preset } from '../main/components/Preset/models';
import Header from '../../components/Header';
import ApproveButton from './components/ApproveButton';
import { Role } from '../../features/authentication/types/auth.types';
import PDFExportButton from '../main/components/PDFExport/Button';
import ExcelExportButton from '../main/components/ExcelExport/Button';
import { Market } from '../main/components/MarketEditor/interfaces';
import { Adjustment } from '../main/components/AdjustmentsEditor/interfaces';
import SendButton from './components/SendButton';
import RejectButton from './components/RejectButton';
import PresetAdjustmentList from './components/PresetAdjustmentList';
import PresetInfo from './components/PresetInfo';
import { TABLES, COLUMNS, VISUAL_IDS } from '../../constants/slicers';
import EditButton from './components/EditButton';

const PresetPage = ({ id }: { id: string }) => {
  const { accessToken, embedUrl, username, hasRole, getRole, canApproveRejectApprover, canApproveRejectCRO } =
    useAuthStore();
  const reportRef1 = useRef<Report | null>(null);
  const reportRef2 = useRef<Report | null>(null);

  const [report1Loaded, setReport1Loaded] = useState<boolean>(false);
  const [report2Loaded, setReport2Loaded] = useState<boolean>(false);

  const [preset, setPreset] = useState<Preset>();

  const [markets, setMarkets] = useState<Market[]>([]);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);

  useEffect(() => {
    trySetReportState();
  }, [report2Loaded, report1Loaded]);

  useEffect(() => {
    if (preset) {
      fetchAdjustments();
      fetchCompanies();
    }
  }, [preset]);

  useEffect(() => {
    fetchPreset();
  }, []);

  const fetchPreset = async () => {
    try {
      const response = await fetch(
        `https://prod-24.eastus.logic.azure.com/workflows/b3e47c86161f426aaca526bbe407bc0d/triggers/manual/paths/invoke/${id}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=BSWhIPqrd-4mxVxVkC8GgVoHbxz6rQ8hHjkOjlq4fDg`
      );
      const data = await response.json();

      setPreset(data[0]);

      trySetReportState();
    } catch (error) {
      console.error('Error fetching preset:', error);
    }
  };

  const trySetReportState = async () => {
    if (report1Loaded && report2Loaded && preset) {
      setCompany(preset.company);
      updateSlider(preset.extraDiscount);
      updateSet(preset.set);
      updatePaint(preset.paintProvided);
      updateDiscountToDisplay(preset.discountToDisplay);
      updateUnitDiscountSlider(preset.unitDiscountEnabled);
      updateDomainname(preset.user);
      updatePaintSlider(preset.paintDiscount);
      updateCleaningSlider(preset.cleanDiscount);
      updateCarpetCleanSlider(preset.carpetCleanDiscount);
      updateMaintenanceSlider(preset.maintenanceDiscount);
      updateSaasFeeSlider(preset.saasUnitFee);
      updateSaasContractedUnits(preset.saasContractedUnits);
      updateSaasFeePeriod(preset.saasFeePeriod);
      updateSaasFeeEnabled(preset.saasFeeEnabled);
    }
  };

  const updateSlicerState = async (
    tableName: string,
    columnName: string,
    slicerName1: string,
    slicerName2: string,
    value: any
  ) => {
    if (!reportRef1.current || !reportRef2.current) {
      console.error('Report container not found.');

      return;
    }

    const report1 = reportRef1.current;
    const report2 = reportRef2.current;

    if (!report1 || !report2) {
      console.error('Report container not found.');
      return;
    }

    const basicFilter: models.ISlicerFilter = {
      $schema: 'http://powerbi.com/product/schema#basic',
      filterType: 1,
      operator: 'In',
      requireSingleSelection: false,
      target: { table: tableName, column: columnName },
      values: [value],
    };

    try {
      const pages1: Page[] = await report1.getPages();
      const pages2: Page[] = await report2.getPages();

      let activePages1 = pages1.filter((page: Page) => page.isActive);
      let activePages2 = pages2.filter((page: Page) => page.isActive);

      const pages = [...activePages1, ...activePages2];

      for (const page of pages) {
        const visuals = await page.getVisuals();
        for (const visual of visuals) {
          if (visual.type === 'slicer' && (visual.name === slicerName1 || visual.name === slicerName2)) {
            try {
              visual.setSlicerState({
                filters: [basicFilter],
              });
            } catch (error) {
              console.error('Error setting slicer state:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error exporting visual data:', error);
    }
  };

  const updateSeviceDiscountSlider = async (sliderName: string, sliceId: string, selection: number) => {
    const series = generateSeries(-1.0, 1.005, 0.005);

    var index = 200 - selection;

    await updateSlicerState(sliderName, sliderName, sliceId, sliceId, series[index]);
  };

  const setCompany = async (name: string) => {
    await updateSlicerState(
      TABLES.MARKET_UNIT_DISTRIBUTION,
      COLUMNS.COMPANY,
      VISUAL_IDS.COMPANY.REPORT1,
      VISUAL_IDS.COMPANY.REPORT2,
      name
    );
  };

  const generateSeries = (start: number, end: number, step: number): number[] => {
    const series: number[] = [];
    for (let value = start; value <= end; value += step) {
      series.push(value);
    }
    return series;
  };

  const updateSlider = async (value: number) => {
    const start = 0;
    const end = 1.005;
    const step = 0.005;
    const series = generateSeries(start, end, step);
    await updateSlicerState(
      TABLES.EXTRA_DISCOUNT,
      COLUMNS.EXTRA_DISCOUNT,
      VISUAL_IDS.EXTRA_DISCOUNT.REPORT1,
      VISUAL_IDS.EXTRA_DISCOUNT.REPORT2,
      series[value]
    );
  };

  const updateSet = async (name: string) => {
    await updateSlicerState(
      TABLES.CALCULATION_METHOD,
      COLUMNS.STATUS,
      VISUAL_IDS.SET_ENABLED.REPORT1,
      VISUAL_IDS.SET_ENABLED.REPORT2,
      name === '' ? 'Off' : 'On'
    );
    await updateSlicerState(
      TABLES.CUSTOM_PRICE_VALUES,
      COLUMNS.SETNAME,
      VISUAL_IDS.SET_VALUE.REPORT1,
      VISUAL_IDS.SET_VALUE.REPORT2,
      name
    );
  };

  const updatePaint = async (selection: string) => {
    await updateSlicerState(
      TABLES.PAINT_PROVIDED_TABLE,
      COLUMNS.PAINT_PROVIDED,
      VISUAL_IDS.PAINT_PROVIDED.REPORT1,
      VISUAL_IDS.PAINT_PROVIDED.REPORT2,
      selection
    );
  };

  const updateDiscountToDisplay = async (selection: string) => {
    await updateSlicerState(
      TABLES.DISCOUNT_DISPLAY_OPTIONS,
      COLUMNS.OPTION,
      VISUAL_IDS.DISCOUNT_DISPLAY.REPORT1,
      VISUAL_IDS.DISCOUNT_DISPLAY.REPORT2,
      selection
    );
  };

  const updateUnitDiscountSlider = async (selection: string) => {
    await updateSlicerState(
      TABLES.UNIT_DISCOUNT_OPTION,
      COLUMNS.OPTION,
      VISUAL_IDS.UNIT_DISCOUNT_ENABLED.REPORT1,
      VISUAL_IDS.UNIT_DISCOUNT_ENABLED.REPORT2,
      selection
    );
  };

  const updateDomainname = async (user: string) => {
    await updateSlicerState(
      TABLES.SYSTEMUSER_PARTITIONED,
      COLUMNS.DOMAINNAME,
      VISUAL_IDS.DOMAINNAME.REPORT1,
      VISUAL_IDS.DOMAINNAME.REPORT2,
      user
    );
  };

  const updatePaintSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.PAINT_DISCOUNT, VISUAL_IDS.PAINT_DISCOUNT, selection);
  };

  const updateCleaningSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.CLEAN_DISCOUNT, VISUAL_IDS.CLEAN_DISCOUNT, selection);
  };

  const updateCarpetCleanSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.CARPET_CLEAN_DISCOUNT, VISUAL_IDS.CARPET_CLEAN_DISCOUNT, selection);
  };

  const updateMaintenanceSlider = async (selection: number) => {
    updateSeviceDiscountSlider(TABLES.MAINTENANCE_DISCOUNT, VISUAL_IDS.MAINTENANCE_DISCOUNT, selection);
  };

  const updateSaasFeeSlider = async (selection: number) => {
    updateSlicerState(
      TABLES.SAAS_UNIT_FEE,
      COLUMNS.SAAS_UNIT_FEE,
      VISUAL_IDS.SAAS_UNIT_FEE,
      VISUAL_IDS.SAAS_UNIT_FEE,
      generateSeries(0, 2.5, 0.01)[selection]
    );
  };

  const updateSaasContractedUnits = async (selection: number) => {
    updateSlicerState(
      TABLES.SAAS_UNIT_COUNT,
      COLUMNS.SAAS_UNIT_COUNT,
      VISUAL_IDS.SAAS_UNIT_COUNT,
      VISUAL_IDS.SAAS_UNIT_COUNT,
      selection
    );
  };

  const updateSaasFeePeriod = async (selection: string) => {
    await updateSlicerState(
      TABLES.SAAS_BILLING_CYCLE,
      COLUMNS.VALUE,
      VISUAL_IDS.SAAS_BILLING_CYCLE,
      VISUAL_IDS.SAAS_BILLING_CYCLE,
      selection
    );
  };

  const updateSaasFeeEnabled = async (selection: string) => {
    await updateSlicerState(
      TABLES.SAAS_FEE_ENABLED,
      COLUMNS.STATUS,
      VISUAL_IDS.SAAS_FEE_ENABLED.REPORT1,
      VISUAL_IDS.SAAS_FEE_ENABLED.REPORT2,
      selection
    );
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(
        `https://prod-89.eastus.logic.azure.com:443/workflows/28f8e528c0764ddfa08d4ad456d49287/triggers/manual/paths/invoke/${preset?.user}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=xkaRvnnRoqQwSn3TlfP92W_Oz0GEnXQ-Hv7ylf0fNso`
      );
      const data = await response.json();

      setMarkets(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchAdjustments = async () => {
    try {
      const response = await fetch(
        `https://prod-49.eastus.logic.azure.com/workflows/dbe186fa92c04549a25406ee1aab0f84/triggers/manual/paths/invoke/${preset?.user}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=vZeMLAAyJUOaFlhm-6wQ34jhYs7A59my5wVkEgXceKs`
      );
      const data = await response.json();

      setAdjustments(data);
    } catch (error) {
      console.error('Error fetching adjustments:', error);
    }
  };

  const showSendForApproval = (): boolean => {
    return false;

    //probably dont need this button at all

    // !isAdminUser(username) && preset?.approvalStatus === ApprovalStatus.PENDING && preset?.user === username;
  };

  const showEdit = (): boolean => {
    return true;
  };

  const canExport = () => {
    if (preset?.approvalStatus === ApprovalStatus.APPROVED_APPROVER) {
      return true;
    }
    if (preset?.approvalStatus === ApprovalStatus.APPROVED_CRO) {
      return preset?.overallDiscount >= 0.0;
    }
    return false;
  };

  const showAnyButtons = (preset: Preset) => {
    return (
      canApproveRejectApprover(preset) ||
      canApproveRejectCRO(preset) ||
      showSendForApproval() ||
      canExport() ||
      showEdit()
    );
  };

  return (
    <div
      style={{
        textAlign: 'center',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header presetId={parseInt(id)} />
      {username ? (
        !preset ? (
          <></>
        ) : !(preset.user === username || hasRole([Role.ADMIN, Role.VIEWER, Role.CRO])) ? (
          <>
            <div
              style={{
                textAlign: 'center',
                fontSize: '2.4vh',
                padding: '2vh',
              }}
            >
              You are not authorized to view this preset.
            </div>
          </>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
              backgroundColor: '#101D3B0D',
              maxHeight: 'calc(100vh - 6.4vh)',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1.6vh 2.4vh 1.6vh 2.4vh',
              }}
            >
              <PresetInfo preset={preset} markets={markets.filter(market => market.company === preset.company)} />
              <div style={{ minHeight: '1.6vh' }}></div>
              <PresetAdjustmentList adjustments={adjustments.filter(adjustment => adjustment.setname === preset.set)} />
            </div>
            <div
              style={{
                flex: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1.6vh 2.4vh 0vh 0vh',
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                  width: '100%',
                }}
              >
                <PowerBIReport
                  embedUrl={embedUrl}
                  accessToken={accessToken}
                  reportRef={reportRef1}
                  onLoad={() => {
                    setReport1Loaded(true);
                  }}
                  username={username}
                />
                <div style={{ width: '1.6vh' }} />
                <PowerBIReport
                  embedUrl={embedUrl}
                  accessToken={accessToken}
                  reportRef={reportRef2}
                  initPage={1}
                  onLoad={() => {
                    setReport2Loaded(true);
                  }}
                  username={username}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: showAnyButtons(preset) ? '8.8vh' : '0vh',
                  width: '100%',
                  backgroundColor: '#fff',
                  borderRadius: '1.6vh 1.6vh 0vh 0vh',
                  gap: '1.6vh',
                }}
              >
                {(canApproveRejectApprover(preset) || canApproveRejectCRO(preset)) && (
                  <>
                    <ApproveButton
                      id={parseInt(id)}
                      username={preset.user}
                      label={preset.name}
                      role={getRole()}
                      needsDiscountApproval={preset.overallDiscount < 0.0}
                    />
                    <RejectButton
                      id={parseInt(id)}
                      username={preset.user}
                      label={preset.name}
                      role={getRole()}
                      onReject={reason => {
                        setPreset({
                          ...preset,
                          approvalStatus: ApprovalStatus.REJECTED_APPROVER,
                          rejectionReason: reason,
                        });
                      }}
                    />
                  </>
                )}
                {showSendForApproval() && <SendButton email={username} label={preset.name} id={parseInt(id)} />}
                {showEdit() && <EditButton id={parseInt(id)} username={preset.user} label={preset.name} />}
                {canExport() && (
                  <>
                    <ExcelExportButton
                      reportRef={reportRef1}
                      discountDisplay={preset.discountToDisplay}
                      paintProvided={preset.paintProvided}
                      selectedSet={preset.set}
                      exportName={preset.name}
                      selectedCompany={preset.company}
                      markets={markets.filter(market => market.company === preset.company)}
                      adjustments={adjustments.filter(adjustment => adjustment.setname === preset.set)}
                    />
                    <div style={{ width: '1.6vh' }} />
                    <PDFExportButton
                      reportRef1={reportRef1}
                      reportRef2={reportRef2}
                      paintProvided={preset.paintProvided === 'Yes'}
                      exportName={preset.name}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
        <></>
      )}
    </div>
  );
};

export default PresetPage;
